'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveCtaLucideByIconKey } from '../../icons/ctaLucideMap';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';

/**
 * Nearest overflow scroller (Design Lab canvas frame) or the window.
 * Lab contains `position:fixed` with transform, so window.scrollY stays 0.
 */
function resolveScrollRoot(node: HTMLElement | null): HTMLElement | Window {
  let current = node?.parentElement ?? null;
  while (current && current !== document.body && current !== document.documentElement) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return current;
    }
    current = current.parentElement;
  }
  return window;
}

function readScrollMetrics(root: HTMLElement | Window) {
  if (root === window) {
    return { y: window.scrollY, viewport: window.innerHeight };
  }
  const el = root as HTMLElement;
  return { y: el.scrollTop, viewport: el.clientHeight };
}

function scrollRootToTop(root: HTMLElement | Window, behavior: ScrollBehavior) {
  if (root === window) {
    window.scrollTo({ top: 0, behavior });
    return;
  }
  (root as HTMLElement).scrollTo({ top: 0, behavior });
}

/**
 * Footer may fill half the viewport — never lift by full intersection height
 * (that parked the FAB mid-page). Cap to the bottom corner band only.
 */
const MAX_FOOTER_CORNER_LIFT_PX = 64;

function footerCornerLiftPx(entry: IntersectionObserverEntry): number {
  if (!entry.isIntersecting) return 0;
  return Math.min(
    MAX_FOOTER_CORNER_LIFT_PX,
    Math.max(0, Math.round(entry.intersectionRect.height)),
  );
}

/**
 * Global chrome — appears after the visitor scrolls past one viewport.
 * Stays in the bottom-right corner; cookie bar (+ small footer nudge) lift only.
 * Studio keeps draft-hidden as a muted ghost; live/staging compose omits it.
 */
export function BackToTopFloat({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  /** Set in effect — never touch `window` during SSR (renderSiteHtml / Node). */
  const scrollRootRef = useRef<HTMLElement | Window | null>(null);
  const [visible, setVisible] = useState(Boolean(editable));
  const isPill = block.registryBlockKey === REGISTRY_BLOCK.presentation.backToTopPill;
  const arrangement = isPill ? 'pill' : 'corner';
  const ariaLabel =
    pickBlockField(block.content, 'ariaLabel', locale) ||
    pickChrome('backToTopAria', locale);
  const iconKey = pickBlockField(block.content, 'sectionIcon', locale);
  const icon = resolveCtaLucideByIconKey(iconKey) ?? ArrowUp;
  const target =
    editable && activeEditTarget
      ? activeEditTarget
      : null;
  const isDraftHidden = block.hidden === true;

  useEffect(() => {
    if (editable) {
      setVisible(true);
      return;
    }
    if (isDraftHidden) {
      setVisible(false);
      return;
    }

    const site = rootRef.current?.closest('.ws-site') as HTMLElement | null;
    const scrollRoot = resolveScrollRoot(rootRef.current);
    scrollRootRef.current = scrollRoot;

    const update = () => {
      const { y, viewport } = readScrollMetrics(scrollRoot);
      const threshold = Math.max(viewport * 0.85, 320);
      setVisible(y > threshold);
    };
    update();

    const scrollTarget: EventTarget = scrollRoot === window ? window : scrollRoot;
    scrollTarget.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const footer =
      site?.querySelector('[data-component="Footer_Standard"]') ??
      document.querySelector('[data-component="Footer_Standard"]');
    let footerObserver: IntersectionObserver | undefined;
    if (footer && site) {
      footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          site.style.setProperty(
            '--ws-float-footer-lift',
            `${footerCornerLiftPx(entry)}px`,
          );
        },
        { threshold: [0, 0.05, 0.15, 0.35, 0.6, 1] },
      );
      footerObserver.observe(footer);
    }

    const cookie = site?.querySelector('.ws-cookie-settings');
    let cookieObserver: MutationObserver | undefined;
    const syncCookieLift = () => {
      if (!site) return;
      const bar = site.querySelector('.ws-cookie-settings:not([hidden])') as HTMLElement | null;
      const lift = bar && bar.offsetParent !== null ? Math.round(bar.getBoundingClientRect().height + 12) : 0;
      site.style.setProperty('--ws-float-cookie-lift', `${lift}px`);
    };
    syncCookieLift();
    if (site) {
      cookieObserver = new MutationObserver(syncCookieLift);
      cookieObserver.observe(site, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'class'],
      });
    }

    return () => {
      scrollTarget.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      footerObserver?.disconnect();
      cookieObserver?.disconnect();
      site?.style.removeProperty('--ws-float-footer-lift');
      site?.style.removeProperty('--ws-float-cookie-lift');
    };
  }, [editable, isDraftHidden]);

  const scrollTop = () => {
    if (editable) return;
    const root = scrollRootRef.current;
    if (!root) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollRootToTop(root, reduce ? 'auto' : 'smooth');
  };

  /* Live / bake omit draft-hidden (matches edge SSR). Studio keeps a muted ghost. */
  if (!editable && isDraftHidden) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={[
        'ws-float-chrome',
        'ws-back-top',
        `ws-back-top--${arrangement}`,
        visible || editable ? 'ws-back-top--visible' : '',
        isActive ? 'ws-block--active' : '',
        isDraftHidden ? 'ws-block--hidden' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-back-top-arrangement={arrangement}
      {...(isDraftHidden ? ({ 'data-block-hidden': 'true' } as const) : {})}
      hidden={!visible && !editable ? true : undefined}
    >
      <button
        type="button"
        className="ws-back-top__btn"
        aria-label={ariaLabel}
        onClick={scrollTop}
      >
        <EditableContent
          as="span"
          fieldKey="sectionIcon"
          editable={editable}
          activeEditTarget={target}
          className="ws-back-top__icon-hit"
        >
          <LucideIcon
            icon={icon}
            size={isPill ? 32 : 'md'}
            className="ws-back-top__icon"
          />
        </EditableContent>
      </button>
    </div>
  );
}
